import { z } from "zod";
import { CatalogSlug, ImageSet, PresetId, Timestamp, Username } from "../../common";

const EXAMPLE = {
  commentId: "019230f4-2c1e-7d3a-9f5b-6a8c0e2d4b17",
  replyId: "019230f6-8b02-7c41-b2d7-3e9a1f5c8d26",
  removedReplyId: "019230f8-1c4d-7e52-a3b8-4f0c2d6e9a37",
  presetId: "3b58d323-cbb8-57dd-ae77-1a9974b5afa5",
  profileId: "7c1f2a9e-4d3b-4f6a-8e2c-1b5d9a7f3e60",
  mentionedProfileId: "a2e4c6b8-0d1f-4a3b-9c5e-7f9a1b3d5e70",
  removedAuthorProfileId: "5d7e9f1a-3b2c-4d8e-9f0a-6c4b2e8d1f73",
  createdAt: "2026-09-08T18:04:11.000Z",
  editedAt: "2026-09-08T18:09:42.000Z",
  cursor: "eyJzIjoidG9wIiwiayI6WzM4LCIwMTkyMzBmNCJdfQ",
} as const;

const exampleSubject = {
  type: "preset",
  id: EXAMPLE.presetId,
  slug: "eve-of-barbarossa",
} as const;

const exampleAuthor = {
  profileId: EXAMPLE.profileId,
  username: "grace",
  displayName: "Grace Hopper",
  avatar: null,
} as const;

const exampleMentionedAuthor = {
  profileId: EXAMPLE.mentionedProfileId,
  username: "ryan",
  displayName: "Ryan",
  avatar: null,
} as const;

const exampleBody =
  "Playing as @[a2e4c6b8-0d1f-4a3b-9c5e-7f9a1b3d5e70] suggested: **Finland** is a great first run.";

/** A reply as a signed-out reader sees it. */
const exampleReply = {
  id: EXAMPLE.replyId,
  parentId: EXAMPLE.commentId,
  status: "published",
  author: exampleMentionedAuthor,
  body: "Seconded. Take the 1939 start and keep Sweden neutral.",
  mentions: [],
  likes: 7,
  replyCount: 0,
  pinnedAt: null,
  createdAt: "2026-09-08T18:12:05.000Z",
  editedAt: null,
} as const;

/** A reply a moderator removed, as everyone but a moderator or its author sees it. */
const exampleRemovedReply = {
  id: EXAMPLE.removedReplyId,
  parentId: EXAMPLE.commentId,
  status: "removed",
  author: {
    profileId: EXAMPLE.removedAuthorProfileId,
    username: null,
    displayName: "Otto",
    avatar: null,
  },
  body: null,
  mentions: [],
  likes: 0,
  replyCount: 0,
  pinnedAt: null,
  createdAt: "2026-09-08T18:20:33.000Z",
  editedAt: null,
} as const;

/** A top-level comment as a signed-out reader sees it. */
const exampleComment = {
  id: EXAMPLE.commentId,
  parentId: null,
  status: "published",
  author: exampleAuthor,
  body: exampleBody,
  mentions: [{ profileId: EXAMPLE.mentionedProfileId, username: "ryan" }],
  likes: 41,
  replyCount: 2,
  pinnedAt: null,
  createdAt: EXAMPLE.createdAt,
  editedAt: null,
} as const;

/** The same comment as its author sees it right after posting. */
const exampleOwnComment = {
  ...exampleComment,
  viewer: { vote: null, canEdit: true, canPin: false },
} as const;

/** Unicode code points, which is what Postgres `length()` and JS `[...text].length` count. */
const BODY_MIN_CODE_POINTS = 1;
const BODY_MAX_CODE_POINTS = 5000;

function codePointLength(text: string): number {
  return [...text].length;
}

export const CommentId = z.uuid().brand<"CommentId">().meta({
  id: "CommentId",
  description:
    "The comment's stable id. Use it as the cache key, to de-duplicate across pages, and in `/api/comments/v1alpha1/{commentId}` routes.",
  example: EXAMPLE.commentId,
});
export type CommentId = z.infer<typeof CommentId>;

export const CommentBody = z
  .string()
  .trim()
  .refine(
    body => {
      const length = codePointLength(body);
      return length >= BODY_MIN_CODE_POINTS && length <= BODY_MAX_CODE_POINTS;
    },
    { message: `body must be ${BODY_MIN_CODE_POINTS}–${BODY_MAX_CODE_POINTS} Unicode code points` },
  )
  .meta({
    id: "CommentBody",
    description:
      "Markdown. Stored as written after trimming; render it with raw HTML disabled. Length is 1–5,000 Unicode code points, which is `[...body].length` in JavaScript. Write `@[profileId]` to mention a profile.",
    example: exampleBody,
    minLength: BODY_MIN_CODE_POINTS,
    maxLength: BODY_MAX_CODE_POINTS,
  });
export type CommentBody = z.infer<typeof CommentBody>;

export const CommentSubject = z
  .strictObject({
    type: z.literal("preset").meta({
      description:
        "What kind of thing the comment is attached to. Switch on it to choose the page and route to render; `preset` is the only kind today.",
    }),
    id: PresetId.meta({
      description:
        "The subject's stable id, for cache keys and for matching the comment to an already-loaded subject.",
      example: EXAMPLE.presetId,
    }),
    slug: CatalogSlug.meta({
      description:
        "The subject's public slug. Use it to build the subject's URL, and as `presetSlug` to list its comments.",
    }),
  })
  .meta({
    id: "CommentSubject",
    description:
      "The thing a comment is attached to. Returned once per response where the caller could not already know it, never repeated per comment.",
    example: exampleSubject,
  });
export type CommentSubject = z.infer<typeof CommentSubject>;

export const CommentAuthor = z
  .strictObject({
    profileId: z.uuid().meta({
      description:
        "The author's public profile id. Link to the profile with it, and use it to build an `@[profileId]` token when replying with a mention.",
      example: EXAMPLE.profileId,
    }),
    username: Username.nullable().meta({
      description:
        "The author's current username, or null before they claim one. Show it as the secondary @username when present.",
      example: "grace",
    }),
    displayName: z.string().meta({
      description: "The author's current display name, shown as the primary name.",
      example: "Grace Hopper",
    }),
    avatar: ImageSet.nullable().meta({
      description:
        "The author's current avatar, or `null` when none is set. Render a placeholder when `null`.",
    }),
  })
  .meta({
    id: "CommentAuthor",
    description:
      "The author's profile as it is right now. Read from the profile at request time and never stored with the comment, so it is always current; display it directly rather than caching it separately.",
  });
export type CommentAuthor = z.infer<typeof CommentAuthor>;

export const CommentMention = z
  .strictObject({
    profileId: z.uuid().meta({
      description:
        "The mentioned profile's id, matching the `@[profileId]` token in `body`. Use it to find the token to replace and to link the rendered mention to the profile.",
      example: EXAMPLE.mentionedProfileId,
    }),
    username: Username.nullable().meta({
      description:
        "The mentioned profile's current username, or null before they claim one. Render a neutral profile label when null.",
      example: "ryan",
    }),
  })
  .meta({
    id: "CommentMention",
    description:
      "One `@[profileId]` token in `body` that named an existing profile. Replace each listed token with a profile link labelled by its username when present; tokens not listed here are plain text.",
  });
export type CommentMention = z.infer<typeof CommentMention>;

export const CommentStatus = z.enum(["published", "deleted", "removed"]).meta({
  id: "CommentStatus",
  description:
    "The comment's state. `published` is live; `deleted` is an author's tombstone. `removed` means a moderator took it down after a report. Tombstones keep their place and author.",
  example: "published",
});
export type CommentStatus = z.infer<typeof CommentStatus>;

/** How many comments a preset's owner may pin at once. */
export const MAX_PINS_PER_SUBJECT = 3;

export const CommentSort = z.enum(["top", "new", "old"]).meta({
  id: "CommentSort",
  description:
    "`top` ranks by net likes and suits the default thread view. `new` and `old` order by posting order, which is id order since ids are UUIDv7; `new` suits a live discussion.",
  example: "top",
});
export type CommentSort = z.infer<typeof CommentSort>;

export const CommentVoteValue = z.enum(["like", "dislike"]).meta({
  id: "CommentVoteValue",
  description:
    "One user's vote on one comment. `like` counts toward the public `likes`; `dislike` affects only the `top` ranking and the moderator-only `dislikes`.",
  example: "like",
});
export type CommentVoteValue = z.infer<typeof CommentVoteValue>;

export const CommentViewer = z
  .strictObject({
    vote: CommentVoteValue.nullable().meta({
      description:
        "The signed-in user's current vote on this comment, or `null`. Use it to highlight the active vote control and as the baseline for optimistic updates.",
      example: "like",
    }),
    canEdit: z.boolean().meta({
      description:
        "Whether the signed-in user may edit and delete this comment: `true` for the author of a published comment.",
      example: false,
    }),
    canPin: z.boolean().meta({
      description:
        "Whether the signed-in user may pin or unpin this comment. `true` only for the preset's owner, and only on a published top-level comment. It says nothing about how many comments are already pinned, so pinning a fourth still returns `409`.",
      example: false,
    }),
  })
  .meta({
    id: "CommentViewer",
    description:
      "The signed-in user's relationship to this comment, for deciding which controls to render. Absent without a session.",
  });
export type CommentViewer = z.infer<typeof CommentViewer>;

export const CommentModeration = z
  .strictObject({
    dislikes: z.number().int().nonnegative().meta({
      description:
        "Dislikes received. Never public; shown so moderators can spot contested comments. Do not surface it in shared UI.",
      example: 2,
    }),
    netLikes: z.number().int().meta({
      description:
        "Likes minus dislikes; the `top` sort key. Explains a comment's position under `top` when `likes` alone would not.",
      example: 39,
    }),
    deletedAt: Timestamp.nullable().meta({
      description:
        "When its author deleted the comment, or `null` if they never did. Set independently of `removedAt`, so both are present when an author deleted a comment a moderator then removed.",
      example: null,
    }),
    removedAt: Timestamp.nullable().meta({
      description:
        "When a moderator removed the comment, or `null` if none did. Show it alongside the tombstone in moderator views.",
      example: null,
    }),
    removedBy: CommentAuthor.nullable().meta({
      description:
        "The moderator who removed the comment, for attributing the action in moderator views. `null` unless `removedAt` is set, or when that moderator's account no longer exists.",
    }),
    revisionCount: z.number().int().nonnegative().meta({
      description:
        "How many earlier versions of `body` are retained. Non-zero signals an edit history exists; this API does not return the earlier bodies.",
      example: 1,
    }),
  })
  .meta({
    id: "CommentModeration",
    description:
      "Reserved for a future comment-moderation permission. This release does not return it.",
  });
export type CommentModeration = z.infer<typeof CommentModeration>;

const commentFields = {
  id: CommentId,
  parentId: CommentId.nullable().meta({
    description:
      "The top-level comment this replies to, or `null` on a top-level comment. Offer Reply only on a published top-level comment.",
    example: null,
  }),
  status: CommentStatus,
  author: CommentAuthor.nullable().meta({
    description:
      "The author's current profile. `null` only when the author's account no longer exists, whatever the comment's `status`; the client decides what to show then, for example \"Deleted account\". Tombstones keep their author.",
  }),
  body: CommentBody.nullable().meta({
    description:
      "The Markdown body, to be rendered with raw HTML disabled. `null` on a tombstone, except that moderators always receive the original text and the author of a `removed` comment receives their own.",
    example: exampleBody,
  }),
  mentions: z.array(CommentMention).meta({
    description:
      "Up to ten distinct profiles named by `@[profileId]` tokens in the visible body, in order of first appearance. Link every occurrence of a listed token; unknown and excess ids stay plain text.",
  }),
  likes: z.number().int().nonnegative().meta({
    description:
      "Likes received; the public count to display. After a vote request, overwrite it with the returned `likes` rather than refetching.",
    example: 41,
  }),
  replyCount: z.number().int().nonnegative().meta({
    description:
      "Replies to this comment, counting tombstones; always 0 for a reply. Show it on the collapsed 'View N replies' control.",
    example: 2,
  }),
  pinnedAt: Timestamp.nullable().meta({
    description:
      "When the preset's owner pinned this comment, or `null` when it is not pinned. Badge a pinned comment; a pinned comment is left out of the sorted pages so it appears once.",
    example: null,
  }),
  createdAt: Timestamp.meta({
    description:
      "When the comment was posted. `new` and `old` sort by `id`, which follows posting order.",
    example: EXAMPLE.createdAt,
  }),
  editedAt: Timestamp.nullable().meta({
    description:
      "When `body` was last edited by its author, or `null` if never. Show an edited marker when set.",
    example: null,
  }),
  viewer: CommentViewer.optional(),
  moderation: CommentModeration.optional(),
};

export const Comment = z.strictObject(commentFields).meta({
  id: "Comment",
  description:
    "One comment. A tombstone keeps its place and author. Its body is hidden, except that the author can see their own future moderator-removed body.",
  example: exampleComment,
});
export type Comment = z.infer<typeof Comment>;

export const TopLevelComment = z
  .strictObject({
    ...commentFields,
    parentId: z.null().meta({
      description:
        "Always `null` for a top-level comment. Replies are loaded separately on demand.",
      example: null,
    }),
  })
  .meta({
    id: "TopLevelComment",
    description: "A top-level comment with a reply count; load its replies on demand.",
    example: exampleComment,
  });
export type TopLevelComment = z.infer<typeof TopLevelComment>;

export const ReplyComment = z
  .strictObject({
    ...commentFields,
    parentId: CommentId.meta({
      description: "The top-level comment this reply belongs to.",
      example: EXAMPLE.commentId,
    }),
  })
  .meta({
    id: "ReplyComment",
    description: "A reply, one level beneath a top-level comment.",
    example: exampleReply,
  });
export type ReplyComment = z.infer<typeof ReplyComment>;

export const CommentIdParams = z
  .strictObject({
    commentId: CommentId.meta({
      description: "The comment to read or act on.",
      example: EXAMPLE.commentId,
    }),
  })
  .meta({
    id: "CommentIdParams",
    description: "A route parameter naming one comment.",
    example: { commentId: EXAMPLE.commentId },
  });
export type CommentIdParams = z.infer<typeof CommentIdParams>;

const PresetSlug = CatalogSlug.meta({ description: "The preset the comments are on." });

const CommentCursor = z.string().min(1).max(512).meta({
  description:
    "Opaque position from a previous page's `nextCursor`. Omit for the first page. Pass it back unchanged; do not construct or decode it.",
  example: EXAMPLE.cursor,
});

export const ListCommentsQuery = z
  .strictObject({
    presetSlug: PresetSlug,
    sort: CommentSort.default("top").meta({
      description:
        "Order of top-level comments; replies are always oldest first. Defaults to `top`. A cursor belongs to one preset and sort; restart from the first page when either changes.",
      example: "top",
    }),
    limit: z.coerce.number().int().min(1).max(10).default(10).meta({
      description:
        "Maximum top-level comments per page, 1–10, default 10. The last page may be shorter; rely on `nextCursor`, not the count, to detect the end.",
      example: 10,
    }),
    cursor: CommentCursor.optional(),
  })
  .meta({
    id: "ListCommentsQuery",
    description: "Which preset's top-level comments to page through, and how.",
    example: { presetSlug: exampleSubject.slug, sort: "top", limit: 10 },
  });
export type ListCommentsQuery = z.infer<typeof ListCommentsQuery>;

export const ListCommentsResponse = z
  .strictObject({
    pinned: z.array(TopLevelComment).meta({
      description:
        "The comments the preset's owner pinned, oldest pin first, at most 3. Render them above `comments`. Returned only on the first page and empty on every later one, because a pinned comment is left out of the sorted pages and so cannot be reached by a cursor.",
    }),
    comments: z.array(TopLevelComment).meta({
      description:
        "One page of top-level comments in the requested order, excluding pinned ones. Replies are collapsed and loaded separately.",
    }),
    nextCursor: z.string().nullable().meta({
      description:
        "Send as `cursor` to fetch the next page, or `null` on the last page. Store it with the page; it is the only reliable end-of-list signal.",
      example: EXAMPLE.cursor,
    }),
    commentCount: z.number().int().nonnegative().meta({
      description:
        "Published comments on the subject, including replies, across all pages; tombstones are not counted. Use it for the count shown on the subject's page; it says nothing about how many pages remain.",
      example: 128,
    }),
  })
  .meta({
    id: "ListCommentsResponse",
    description: "A page of a subject's comments, led by whatever its owner pinned.",
    example: {
      pinned: [],
      comments: [exampleComment],
      nextCursor: EXAMPLE.cursor,
      commentCount: 128,
    },
  });
export type ListCommentsResponse = z.infer<typeof ListCommentsResponse>;

export const ListRepliesQuery = z
  .strictObject({
    limit: z.coerce.number().int().min(1).max(5).default(5).meta({
      description:
        "Maximum replies per page, 1–5, default 5. The last page may be shorter; rely on `nextCursor`, not the count, to detect the end.",
      example: 5,
    }),
    cursor: CommentCursor.optional(),
  })
  .meta({
    id: "ListRepliesQuery",
    description:
      "How to page through one comment's replies. Without a cursor, paging starts at the oldest reply and moves newer. A reply's `siblings` from get comment carry cursors that start at that reply and move older or newer.",
    example: { limit: 5 },
  });
export type ListRepliesQuery = z.infer<typeof ListRepliesQuery>;

export const ListRepliesResponse = z
  .strictObject({
    replies: z.array(ReplyComment).meta({
      description:
        "Replies to the top-level comment, oldest first within the page, including a page of older replies.",
    }),
    nextCursor: z.string().nullable().meta({
      description:
        "Send as `cursor` to fetch the next page in the same direction, or `null` when none remain. It is the only reliable end-of-list signal.",
      example: null,
    }),
  })
  .meta({
    id: "ListRepliesResponse",
    description: "A page of one comment's replies.",
    example: { replies: [exampleReply, exampleRemovedReply], nextCursor: null },
  });
export type ListRepliesResponse = z.infer<typeof ListRepliesResponse>;

export const ReplySiblings = z
  .strictObject({
    olderCount: z.number().int().nonnegative().meta({
      description: "Replies to the same parent posted before this one.",
      example: 3,
    }),
    newerCount: z.number().int().nonnegative().meta({
      description: "Replies to the same parent posted after this one.",
      example: 5,
    }),
    olderCursor: z.string().nullable().meta({
      description:
        "Send to list replies to load the replies just before this one, or `null` when there are none.",
      example: EXAMPLE.cursor,
    }),
    newerCursor: z.string().nullable().meta({
      description:
        "Send to list replies to load the replies just after this one, or `null` when there are none.",
      example: null,
    }),
  })
  .meta({
    id: "ReplySiblings",
    description:
      "Where a reply sits among its parent's other replies, with cursors that page outward from it.",
    example: { olderCount: 3, newerCount: 0, olderCursor: EXAMPLE.cursor, newerCursor: null },
  });
export type ReplySiblings = z.infer<typeof ReplySiblings>;

export const GetCommentResponse = z
  .strictObject({
    comment: Comment,
    parent: TopLevelComment.nullable().meta({
      description:
        "The top-level comment the requested comment replies to, or `null` when it is itself top-level. Render it above the reply so a permalink shows what was being answered; without it a reply's page would need a second request to `parentId` before it could draw anything.",
    }),
    siblings: ReplySiblings.nullable().meta({
      description:
        "For a reply, the parent's other replies on either side of it, so a permalink can show the reply in place and load older and newer replies around it. `null` for a top-level comment.",
    }),
    subject: CommentSubject,
  })
  .meta({
    id: "GetCommentResponse",
    description:
      "One comment with the thread and subject around it, so a deep link renders its page from a single request.",
    example: { comment: exampleComment, parent: null, siblings: null, subject: exampleSubject },
  });
export type GetCommentResponse = z.infer<typeof GetCommentResponse>;

export const CreateCommentRequest = z
  .strictObject({
    presetSlug: PresetSlug,
    body: CommentBody,
    parentId: CommentId.optional().meta({
      description:
        "The `id` of a published top-level comment on this preset; omit to start a new thread. A reply id returns 400, and a deleted or removed parent returns 409.",
      example: EXAMPLE.commentId,
    }),
  })
  .meta({
    id: "CreateCommentRequest",
    description: "A new comment or reply on a preset.",
    example: { presetSlug: exampleSubject.slug, body: exampleBody },
  });
export type CreateCommentRequest = z.infer<typeof CreateCommentRequest>;

export const CreateCommentResponse = z
  .strictObject({
    comment: Comment,
  })
  .meta({
    id: "CreateCommentResponse",
    description:
      "The comment as it was stored, including `viewer` and resolved `mentions`. Show a new reply immediately; append it to the chronological list only when all earlier replies are loaded.",
    example: { comment: { ...exampleOwnComment, likes: 0, replyCount: 0 } },
  });
export type CreateCommentResponse = z.infer<typeof CreateCommentResponse>;

export const UpdateCommentRequest = z
  .strictObject({
    body: CommentBody,
  })
  .meta({
    id: "UpdateCommentRequest",
    description: "The replacement body. The previous body is retained as a revision.",
    example: { body: "Edited: **Finland** is a great first run, but start with the tutorial." },
  });
export type UpdateCommentRequest = z.infer<typeof UpdateCommentRequest>;

export const UpdateCommentResponse = z
  .strictObject({
    comment: Comment,
  })
  .meta({
    id: "UpdateCommentResponse",
    description:
      "The comment after the edit. Replace the cached comment with it; `editedAt` is set and `mentions` are re-resolved from the new body.",
    example: {
      comment: {
        ...exampleOwnComment,
        body: "Edited: **Finland** is a great first run, but start with the tutorial.",
        mentions: [],
        editedAt: EXAMPLE.editedAt,
      },
    },
  });
export type UpdateCommentResponse = z.infer<typeof UpdateCommentResponse>;

export const SetCommentVoteRequest = z
  .strictObject({
    value: CommentVoteValue,
  })
  .meta({
    id: "SetCommentVoteRequest",
    description:
      "The signed-in user's vote. Sending a different value replaces the previous vote; sending the same value again is a no-op that still returns the current count.",
    example: { value: "like" },
  });
export type SetCommentVoteRequest = z.infer<typeof SetCommentVoteRequest>;

export const CommentVoteCount = z
  .strictObject({
    likes: z.number().int().nonnegative().meta({
      description:
        "The comment's likes after this request. Write it into the cached comment's `likes`; it already reflects the vote just sent.",
      example: 42,
    }),
  })
  .meta({
    id: "CommentVoteCount",
    description:
      "The authoritative like count after a vote request. The caller already knows its own vote from the request it sent; patch the cached comment's `likes` from this rather than refetching the list.",
    example: { likes: 42 },
  });
export type CommentVoteCount = z.infer<typeof CommentVoteCount>;
